/**
 * Spec 083 — Admin → Page Evaluations (list).
 * Spec 093 — Bulk delete (multi-select + sticky action bar + confirm modal).
 *
 * Brand-scoped list of page evaluations with filters (status, date range,
 * score range) and pagination. Rows click through to the detail page.
 *
 * Phase 9 Session C gap 083-1.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Icon, Spinner } from '../../components/ui/Icon.js';
import { useBrand } from '../../lib/brand-context.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ScoreBadge } from '../../components/ScoreBadge.js';
import {
  bulkDeleteEvaluations,
  deleteEvaluation,
  getPageEvaluationsReport,
  listEvaluations,
  type PageEvaluationListItem,
  type PageEvalStatus,
} from '../../api/pageEvaluations.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { RoleProvisioningBanner } from '../../components/RoleProvisioningBanner.js';
import { DateInput } from '../../components/DateInput.js';
import { Select } from '../../components/Select.js';
import { SkeletonRows } from '../../components/Skeleton.js';
import { Button } from '../../components/ui/Button.js';
import { useFullBleed } from '../../lib/layout-width.js';

const PAGE_SIZE = 20;

const STATUS_OPTIONS: readonly PageEvalStatus[] = [
  'pending',
  'running',
  'completed',
  'failed',
] as const;

const STATUS_STYLES: Record<PageEvalStatus, string> = {
  pending: 'bg-amber-100 text-amber-800',
  running: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-rose-100 text-rose-800',
};

interface ActionMessage {
  kind: 'success' | 'error';
  text: string;
}

/** Spec 130 — brand name → filename-safe slug (mirrors BrandReportButton). */
function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'brand'
  );
}

export function PageEvaluationsPage() {
  useFullBleed();
  const { selectedBrand, loading: brandsLoading } = useBrand();
  const navigate = useNavigate();
  const brandId = selectedBrand?.id ?? null;

  const [status, setStatus] = useState<PageEvalStatus | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [minScore, setMinScore] = useState<string>('');
  const [maxScore, setMaxScore] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<PageEvaluationListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Spec 093 — bulk delete state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);

  // Spec 130 — markdown export state
  const [exporting, setExporting] = useState(false);

  const offset = page * PAGE_SIZE;

  useEffect(() => {
    if (!brandId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const minScoreNum = minScore === '' ? undefined : Number(minScore);
    const maxScoreNum = maxScore === '' ? undefined : Number(maxScore);
    listEvaluations(brandId, {
      status: status || undefined,
      from: from || undefined,
      to: to || undefined,
      min_score: Number.isFinite(minScoreNum) ? minScoreNum : undefined,
      max_score: Number.isFinite(maxScoreNum) ? maxScoreNum : undefined,
      limit: PAGE_SIZE,
      offset,
    })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data);
        setTotal(res.total);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brandId, status, from, to, minScore, maxScore, offset, reloadToken]);

  // Spec 093 — auto-clear selection whenever the visible list changes
  // (filter / page / brand change). Reuses the same dep set as the data
  // fetch so we never end up with a selection that refers to rows the user
  // can no longer see.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [brandId, status, from, to, minScore, maxScore, page]);

  // Spec 093 — auto-dismiss the transient action message after a few seconds
  // so the operator isn't left staring at stale "Deleted 3 evaluations" copy.
  useEffect(() => {
    if (!actionMessage) return;
    const t = setTimeout(() => setActionMessage(null), 4000);
    return () => clearTimeout(t);
  }, [actionMessage]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / PAGE_SIZE)),
    [total],
  );

  // Spec 093 — header checkbox indeterminate logic
  const allOnPageSelected =
    rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someOnPageSelected = rows.some((r) => selectedIds.has(r.id));
  const headerIndeterminate = someOnPageSelected && !allOnPageSelected;
  const headerCheckboxRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = headerIndeterminate;
    }
  }, [headerIndeterminate]);

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleHeader() {
    setSelectedIds((prev) => {
      if (allOnPageSelected) return new Set();
      const next = new Set(prev);
      for (const r of rows) next.add(r.id);
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  async function handleDelete(row: PageEvaluationListItem) {
    const label = row.page_url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 60);
    const ok = window.confirm(
      `Permanently delete the evaluation for "${label}"?\n\n` +
        `This removes the evaluation and all its rule results and snapshots. ` +
        `This cannot be undone.`,
    );
    if (!ok) return;
    setDeletingId(row.id);
    setError(null);
    try {
      await deleteEvaluation(row.id);
      setReloadToken((t) => t + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeletingId(null);
    }
  }

  async function handleBulkDelete() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkSubmitting(true);
    try {
      const res = await bulkDeleteEvaluations(ids);
      setConfirmOpen(false);
      setSelectedIds(new Set());
      setReloadToken((t) => t + 1);
      const msg =
        res.skipped_ids.length > 0
          ? `Deleted ${res.deleted_count} of ${ids.length} — others were already gone`
          : `Deleted ${res.deleted_count} evaluation${
              res.deleted_count === 1 ? '' : 's'
            }`;
      setActionMessage({ kind: 'success', text: msg });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionMessage({ kind: 'error', text: `Bulk delete failed: ${msg}` });
      // Modal stays open + selection intact so operator can retry.
    } finally {
      setBulkSubmitting(false);
    }
  }

  // Spec 130 — fetch the markdown report and trigger a client-side download.
  // Blob-download logic mirrors BrandReportButton.handleDownload.
  async function handleExport() {
    if (!selectedBrand || !brandId) return;
    setExporting(true);
    try {
      const report = await getPageEvaluationsReport(brandId);
      const blob = new Blob([report.markdown], {
        type: 'text/markdown;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `${slugify(selectedBrand.name)}-page-evaluations-${dateStr}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setActionMessage({ kind: 'error', text: 'Failed to export report' });
    } finally {
      setExporting(false);
    }
  }

  // Phase 9 Session E.2 (gap XC-R2-1): outer spinner short-circuit so we
  // don't flash "No brand selected" while the brand list is still loading.
  if (brandsLoading) {
    return (
      <div
        data-testid="brands-loading"
        className="flex flex-col items-center justify-center py-16 text-slate-500"
      >
        <Spinner className="text-violet-600" />
        <p className="mt-3 text-sm">Loading brands…</p>
      </div>
    );
  }

  if (!selectedBrand) {
    return (
      <div data-testid="admin-page-evaluations" className="hub-page--table" data-density="compact">
        <RoleProvisioningBanner />
        <Breadcrumbs
          items={[
            { label: 'Observatory', to: '/admin' },
            { label: 'Page evaluations' },
          ]}
        />
        <h1 className="hub-page__title flex items-center gap-2">
          <Icon name="bx-search-alt" />
          Page evaluations
        </h1>
        <EmptyState
          title="No brand selected"
          description="Select a brand to view page evaluations."
        />
      </div>
    );
  }

  const selectionCount = selectedIds.size;

  return (
    <div data-testid="admin-page-evaluations" className="hub-page--table" data-density="compact">
      <RoleProvisioningBanner />
      <Breadcrumbs
        items={[
          { label: 'Observatory', to: '/admin' },
          { label: 'Page evaluations' },
        ]}
      />
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title flex items-center gap-2">
            <Icon name="bx-search-alt" />
            Page evaluations
          </h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">
            Rule-level evaluation results for pages crawled from {selectedBrand.name}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {total >= 1 && (
            <Button
              variant="ghost"
              data-testid="export-evaluations-md"
              onClick={() => void handleExport()}
              disabled={exporting}
            >
              {exporting ? <Spinner /> : <Icon name="bx-download" />}
              {exporting ? 'Exporting…' : 'Export Markdown'}
            </Button>
          )}
          <Link to="/admin/evaluations/new" data-testid="page-evaluations-new-cta" className="hub-btn hub-btn--primary">
            New evaluation
          </Link>
        </div>
      </div>

      <div className="hub-toolbar">
        <label className="text-sm">
          <span className="mb-1 block text-[var(--hub-color-ink-muted)]">Status</span>
          <Select
            data-testid="page-evaluations-filter-status"
            value={status}
            onChange={(v) => {
              setStatus(v as PageEvalStatus | '');
              setPage(0);
            }}
            options={[
              { value: '', label: 'any' },
              ...STATUS_OPTIONS.map((s) => ({ value: s, label: s })),
            ]}
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-[var(--hub-color-ink-muted)]">From</span>
          <DateInput
            data-testid="page-evaluations-filter-from"
            value={from}
            onChange={(v) => {
              setFrom(v);
              setPage(0);
            }}
            className="w-full"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-[var(--hub-color-ink-muted)]">To</span>
          <DateInput
            data-testid="page-evaluations-filter-to"
            value={to}
            onChange={(v) => {
              setTo(v);
              setPage(0);
            }}
            className="w-full"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-[var(--hub-color-ink-muted)]">Min score</span>
          <input
            data-testid="page-evaluations-filter-min-score"
            type="number"
            min={0}
            max={100}
            value={minScore}
            onChange={(e) => {
              setMinScore(e.target.value);
              setPage(0);
            }}
            className="hub-input w-full"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-[var(--hub-color-ink-muted)]">Max score</span>
          <input
            data-testid="page-evaluations-filter-max-score"
            type="number"
            min={0}
            max={100}
            value={maxScore}
            onChange={(e) => {
              setMaxScore(e.target.value);
              setPage(0);
            }}
            className="hub-input w-full"
          />
        </label>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      )}

      {actionMessage && (
        <div
          data-testid={`bulk-action-message-${actionMessage.kind}`}
          className={
            actionMessage.kind === 'success'
              ? 'rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800'
              : 'rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800'
          }
        >
          {actionMessage.text}
        </div>
      )}

      {/* Spec 093 — bulk action bar. Not sticky: spec 232 spends sticky on
          exactly two things (the bar, a thead), and this page already has a
          sticky thead below. */}
      {selectionCount > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="flex items-center justify-between gap-3 rounded-md border border-[var(--hub-color-line)] bg-[var(--hub-color-action-soft)] px-4 py-2.5 text-sm"
        >
          <div className="flex items-center gap-3">
            <span data-testid="bulk-selection-count" className="font-semibold">
              {selectionCount} selected
            </span>
            <button
              type="button"
              data-testid="bulk-selection-clear"
              onClick={clearSelection}
              className="text-[var(--hub-color-action-text)]"
            >
              Clear
            </button>
          </div>
          <Button
            variant="danger"
            data-testid="bulk-delete-button"
            onClick={() => setConfirmOpen(true)}
            disabled={bulkSubmitting}
          >
            <Icon name="bx-trash" />
            Delete {selectionCount} selected
          </Button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="hub-table" data-testid="page-evaluations-table">
          <thead>
            <tr>
              <th className="w-10">
                <input
                  ref={headerCheckboxRef}
                  type="checkbox"
                  data-testid="bulk-header-checkbox"
                  checked={allOnPageSelected}
                  onChange={toggleHeader}
                  disabled={rows.length === 0}
                  aria-label="Select all on this page"
                />
              </th>
              <th>URL</th>
              <th>Status</th>
              <th>Score</th>
              <th>Rules</th>
              <th>Content type</th>
              <th>Started</th>
              <th>Completed</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9}>
                  <SkeletonRows rows={8} cols={6} />
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-[var(--hub-color-ink-muted)]">
                  <div className="font-medium text-[var(--hub-color-ink)]">No evaluations yet.</div>
                  <div className="mt-1 text-sm">
                    Submit a URL to get started.
                  </div>
                  <Link to="/admin/evaluations/new" className="hub-btn hub-btn--ghost mt-3 inline-flex">
                    New evaluation
                  </Link>
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((r) => {
                const checked = selectedIds.has(r.id);
                return (
                  <tr
                    key={r.id}
                    data-testid={`page-evaluation-row-${r.id}`}
                    onClick={() => navigate(`/admin/evaluations/${r.id}`)}
                    className={`cursor-pointer ${checked ? 'bg-[var(--hub-color-action-soft)]' : ''}`}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        data-testid={`page-evaluation-row-checkbox-${r.id}`}
                        checked={checked}
                        onChange={() => toggleRow(r.id)}
                        aria-label={`Select evaluation ${r.id}`}
                      />
                    </td>
                    <td className="max-w-xs truncate font-mono text-xs">
                      <span title={r.page_url}>
                        {r.page_url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 60)}
                      </span>
                      {r.partial_completion && (
                        <span
                          title="Partial completion — some rules were not evaluated"
                          className="ml-2 inline-block h-2 w-2 rounded-full bg-amber-500 align-middle"
                          aria-label="Partial completion"
                        />
                      )}
                    </td>
                    <td>
                      <StatusBadge status={r.status} />
                    </td>
                    <td>
                      <ScoreBadge
                        scoreType="QualityScore"
                        value={r.aggregate_score}
                        size="sm"
                      />
                    </td>
                    <td className="text-xs">
                      <span className="hub-chip hub-chip--ok">
                        {r.rules_passed} pass
                      </span>{' '}
                      <span className="hub-chip hub-chip--fail">
                        {r.rules_failed} fail
                      </span>{' '}
                      <span className="hub-chip hub-chip--warn">
                        {r.rules_partial} partial
                      </span>
                    </td>
                    <td className="text-xs text-[var(--hub-color-ink-muted)]">
                      {r.content_type_detected ?? (
                        <span className="text-[var(--hub-color-ink-subtle)]">—</span>
                      )}
                    </td>
                    <td className="text-xs text-[var(--hub-color-ink-muted)]">
                      {new Date(r.started_at).toLocaleString()}
                    </td>
                    <td className="text-xs text-[var(--hub-color-ink-muted)]">
                      {r.completed_at ? (
                        new Date(r.completed_at).toLocaleString()
                      ) : (
                        <span className="text-[var(--hub-color-ink-subtle)]">—</span>
                      )}
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDelete(r);
                        }}
                        disabled={deletingId === r.id}
                        data-testid={`page-evaluation-delete-${r.id}`}
                        title="Delete evaluation"
                        aria-label="Delete evaluation"
                        className="hub-btn hub-btn--quiet"
                      >
                        {deletingId === r.id ? (
                          <Spinner />
                        ) : (
                          <Icon name="bx-trash" />
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-[var(--hub-color-ink-muted)]">
        <span>
          Showing {total === 0 ? 0 : offset + 1}-
          {Math.min(total, offset + rows.length)} of {total}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            data-testid="page-evaluations-prev"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Prev
          </Button>
          <span>
            Page {page + 1} / {totalPages}
          </span>
          <Button
            variant="ghost"
            data-testid="page-evaluations-next"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      {confirmOpen && (
        <div
          data-testid="bulk-delete-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-delete-modal-title"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6">
            <div className="mb-3 flex items-center gap-2">
              <Icon name="bx-error" className="text-rose-600" />
              <h2
                id="bulk-delete-modal-title"
                className="text-lg font-semibold text-slate-900"
              >
                Permanently delete {selectionCount} evaluation
                {selectionCount === 1 ? '' : 's'}?
              </h2>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={bulkSubmitting}
                aria-label="Close"
                className="ml-auto rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
              >
                <Icon name="bx-x" />
              </button>
            </div>
            <p className="text-sm text-slate-700">
              This is <strong>irreversible</strong>. Rule results, content
              snapshots, and job rows will also be deleted.
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                data-testid="bulk-delete-cancel"
                onClick={() => setConfirmOpen(false)}
                disabled={bulkSubmitting}
                className="hub-btn hub-btn--ghost"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="bulk-delete-confirm"
                onClick={() => void handleBulkDelete()}
                disabled={bulkSubmitting}
                className="hub-btn hub-btn--danger"
              >
                {bulkSubmitting && (
                  <Spinner  />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: PageEvalStatus }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  );
}
